// DetailCommande.js
import { Link, useParams } from "react-router-dom";
import { CSSProperties, useEffect, useState } from "react";
import { Commande, detailcommande } from "../../../Modeles/Commande";
import DetailCommandeService from "../../../Services/Admin/DetailCommandeService";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";
import PayArticleModal from "../../Common/PayArticleModal";

export interface CommandeType {
  product: detailcommande[];
  messageErrors: string;
}

export default function DetailCommandeMagasin({
  MessageSucess,
  MessageError,
  handleSubmitUpdate,
  handleAnnuleCommande,
}: {
  MessageSucess?: string;
  handleSubmitUpdate?: (ref: string, nommagasin: string, qte: number, id: any) => Promise<any> | any;
  handleAnnuleCommande?: (id: number) => Promise<any> | any;
  MessageError?: string;
}) {
  const MagasinName = localStorage.getItem("MagasinName");
  const { id } = useParams();
  const [state, setState] = useState<CommandeType>({
    product: [] as detailcommande[],
    messageErrors: "Aucune commande",
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [itemToPay, setItemToPay] = useState<detailcommande | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await DetailCommandeService(id).getCommande();
        setState((prev) => ({ ...prev, product: res.data }));
      } catch (error) {
        setState((prev) => ({ ...prev, messageErrors: "Erreur de chargement" }));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { product } = state;

  const [confirmPopup, setConfirmPopup] = useState(false);

  const handleConfirmClick = async (ref: string, vendeur: string, quantity: number, idCommande: any, itemId: any) => {
    if (handleSubmitUpdate) {
      const key = `${itemId}_confirm`;
      setActionLoading((prev) => ({ ...prev, [key]: true }));
      setConfirmPopup(true);
      try {
        await handleSubmitUpdate(ref, vendeur, quantity, itemId);
        setState((prev) => ({
          ...prev,
          product: prev.product.map((p) =>
            p.id === itemId ? { ...p, Statut: "confirmé" } : p
          ),
        }));
      } finally {
        setActionLoading((prev) => ({ ...prev, [key]: false }));
        setConfirmPopup(false);
      }
    }
  };

  const handleAnnulerClick = async (id: number) => {
    if (handleAnnuleCommande) {
      const key = `${id}_annule`;
      setActionLoading((prev) => ({ ...prev, [key]: true }));
      try {
        await handleAnnuleCommande(id);
        setState((prev) => ({
          ...prev,
          product: prev.product.map((p) =>
            p.id === id ? { ...p, Statut: "annulé" } : p
          ),
        }));
      } finally {
        setActionLoading((prev) => ({ ...prev, [key]: false }));
      }
    }
  };

  const handleGoBack = () => window.history.back();

  /* ─── DESIGN TOKENS ───────────────────────────────────────────── */
  const C = {
    bg: "#ffffff",
    surface: "#ffffff",
    surfaceAlt: "#feffaaee",
    border: "#e7e7e7",
    borderLight: "#2e3447",
    accent: "#e2ad00e8",
    accentDim: "rgba(255,196,0,0.12)",
    accentGlow: "rgba(255,196,0,0.25)",
    textPrimary: "#292929",
    textSecondary: "#FFC400",
    textMuted: "#FFC400",
    green: "#22c55e",
    greenDim: "rgba(34,197,94,0.12)",
    red: "#ef4444",
    redDim: "rgba(239,68,68,0.12)",
    orange: "#f97316",
    orangeDim: "rgba(249,115,22,0.12)",
  };

  /* ─── SHARED STYLES ────────────────────────────────────────────── */
  const s: Record<string, CSSProperties> = {
    wrapper: {
      minHeight: "100vh",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      color: C.textPrimary,
    },
    main: {
      marginLeft: isMobile ? 0 : "260px",
      padding: isMobile ? "16px 12px" : "40px 32px",
      maxWidth: "1200px",
    },

    /* Header */
    header: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "32px",
    },
    backBtn: {
      width: "40px",
      height: "40px",
      borderRadius: "10px",
      border: `1px solid ${C.border}`,
      backgroundColor: C.surface,
      color: C.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "22px",
      transition: "all 0.2s",
      flexShrink: 0,
    },
    titleWrapper: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "2px",
    },
    title: {
      fontSize: isMobile ? "18px" : "22px",
      fontWeight: 700,
      margin: 0,
      letterSpacing: "-0.3px",
      color: C.textPrimary,
    },
    subtitle: {
      fontSize: "13px",
      color: C.textSecondary,
      margin: 0,
    },
    accentLine: {
      width: "36px",
      height: "3px",
      backgroundColor: C.accent,
      borderRadius: "2px",
      marginTop: "4px",
    },

    /* Loader */
    loaderWrap: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      height: "400px",
      gap: "16px",
    },
    loaderRing: {
      width: "44px",
      height: "44px",
      border: `3px solid ${C.border}`,
      borderTop: `3px solid ${C.accent}`,
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
    },
    loaderText: {
      fontSize: "13px",
      color: C.textSecondary,
      letterSpacing: "0.5px",
    },

    /* ── TABLE ── */
    tableCard: {
      backgroundColor: C.surface,
      borderRadius: "16px",
      border: `1px solid ${C.border}`,
      overflow: "hidden",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      fontSize: "14px",
    },
    thead: {
      backgroundColor: C.surfaceAlt,
    },
    th: {
      padding: "14px 18px",
      textAlign: "left" as const,
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "uppercase" as const,
      letterSpacing: "0.8px",
      color: C.textMuted,
      borderBottom: `1px solid ${C.border}`,
      whiteSpace: "nowrap" as const,
    },
    td: {
      padding: "14px 18px",
      borderBottom: `1px solid ${C.border}`,
      color: C.textPrimary,
      verticalAlign: "middle" as const,
    },
    tdMuted: {
      padding: "14px 18px",
      borderBottom: `1px solid ${C.border}`,
      color: C.textSecondary,
      verticalAlign: "middle" as const,
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "13px",
    },
    idBadge: {
      display: "inline-block",
      backgroundColor: C.accentDim,
      color: C.accent,
      padding: "2px 10px",
      borderRadius: "6px",
      fontWeight: 700,
      fontSize: "13px",
      fontFamily: "'JetBrains Mono', monospace",
    },
    priceBadge: {
      fontWeight: 600,
      color: C.textPrimary,
    },
    qtyBadge: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "32px",
      height: "28px",
      backgroundColor: C.surfaceAlt,
      border: `1px solid ${C.border}`,
      borderRadius: "6px",
      fontWeight: 600,
      fontSize: "13px",
      color: C.textPrimary,
      padding: "0 8px",
    },
    emptyRow: {
      padding: "60px 20px",
      textAlign: "center" as const,
      color: C.textMuted,
      fontSize: "14px",
    },

    /* ── MOBILE CARD ── */
    card: {
      backgroundColor: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: "14px",
      marginBottom: "14px",
      overflow: "hidden",
    },
    cardHeader: {
      backgroundColor: C.surfaceAlt,
      padding: "10px 14px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: `1px solid ${C.border}`,
    },
    cardRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 14px",
      borderBottom: `1px solid ${C.border}`,
    },
    cardLabel: {
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "uppercase" as const,
      letterSpacing: "0.6px",
      color: C.textMuted,
    },
    cardValue: {
      fontSize: "14px",
      color: C.textPrimary,
      textAlign: "right" as const,
      maxWidth: "60%",
    },
    cardActions: {
      display: "flex",
      gap: "8px",
      padding: "12px 14px",
    },

    /* ── BUTTONS ── */
    btnConfirm: {
      flex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      backgroundColor: C.greenDim,
      color: C.green,
      border: `1px solid rgba(34,197,94,0.3)`,
      padding: "8px 16px",
      borderRadius: "8px",
      fontWeight: 600,
      fontSize: "13px",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    btnCancel: {
      flex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      backgroundColor: C.redDim,
      color: C.red,
      border: `1px solid rgba(239,68,68,0.3)`,
      padding: "8px 16px",
      borderRadius: "8px",
      fontWeight: 600,
      fontSize: "13px",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    tdActions: {
      padding: "10px 14px",
      borderBottom: `1px solid ${C.border}`,
      verticalAlign: "middle" as const,
    },
    actionGroup: {
      display: "flex",
      gap: "8px",
    },
  };

  /* ─── STATUS BADGE ─────────────────────────────────────────────── */
  const StatutBadge = ({ statut }: { statut: string }) => {
    const sLower = (statut || "").toLowerCase();
    let bg = C.orangeDim, color = C.orange, border = "rgba(249,115,22,0.3)", dot = "●";
    if (sLower.includes("pay") || sLower.includes("confirm") || sLower.includes("valid")) {
      bg = C.greenDim; color = C.green; border = "rgba(34,197,94,0.3)"; dot = "✓";
    } else if (sLower.includes("annul")) {
      bg = C.redDim; color = C.red; border = "rgba(239,68,68,0.3)"; dot = "✕";
    }
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        backgroundColor: bg, color, border: `1px solid ${border}`,
        padding: "3px 10px", borderRadius: "20px",
        fontSize: "12px", fontWeight: 600,
      }}>
        <span style={{ fontSize: "8px" }}>{dot}</span>
        {statut}
      </span>
    );
  };

  /* ─── LOADER ───────────────────────────────────────────────────── */
  const Loader = () => (
    <div style={s.loaderWrap}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={s.loaderRing} />
      <span style={s.loaderText}>Chargement des commandes…</span>
    </div>
  );

  const renderActions = (pro: any) => {
    const sLower = (pro.Statut || "").toLowerCase();
    if (sLower.includes("annul")) {
      return <span style={{ color: C.textMuted, fontSize: "12px", fontWeight: 500 }}>—</span>;
    }

    const isOwnArticle = pro.vendeur && MagasinName && pro.vendeur.trim().toLowerCase() === MagasinName.trim().toLowerCase();

    // If this article belongs to the current logged-in magasin (we are the seller)
    if (isOwnArticle) {
      if (sLower.includes("confirm") || sLower.includes("valid") || sLower.includes("pay")) {
        return <span style={{ color: C.green, fontSize: "12px", fontWeight: 600 }}>✓ Confirmé</span>;
      }
      const isLoadingConfirm = actionLoading[`${pro.id}_confirm`];
      const isLoadingAnnule = actionLoading[`${pro.id}_annule`];
      const isAnyLoading = isLoadingConfirm || isLoadingAnnule;

      return (
        <div style={s.actionGroup}>
          <button 
            style={{ ...s.btnConfirm, opacity: isAnyLoading ? 0.6 : 1 }}
            disabled={isAnyLoading}
            onClick={() => handleConfirmClick(pro.RefArticle, pro.vendeur, pro.quantity, pro.idCommande, pro.id)}
          >
            {isLoadingConfirm ? <i className="bi bi-hourglass-bottom"></i> : "✓ Confirmer"}
          </button>
          <button 
            style={{ ...s.btnCancel, opacity: isAnyLoading ? 0.6 : 1 }}
            disabled={isAnyLoading}
            onClick={() => handleAnnulerClick(pro.id)}
          >
            {isLoadingAnnule ? <i className="bi bi-hourglass-bottom"></i> : "✕ Annuler"}
          </button>
        </div>
      );
    }

    // Article belongs to ANOTHER magasin (we are the buyer / commercial's parent magasin)
    if (sLower.includes("pay") || sLower.includes("confirm")) {
      return (
        <span style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          backgroundColor: C.greenDim,
          color: C.green,
          border: `1px solid rgba(34,197,94,0.3)`,
          padding: "4px 10px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 600,
        }}>
          ✓ Payé
        </span>
      );
    }

    // Not yet paid: Show "Payer" button
    return (
      <button
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          backgroundColor: "#2563eb",
          color: "#ffffff",
          border: "none",
          padding: "7px 14px",
          borderRadius: "8px",
          fontWeight: 700,
          fontSize: "12px",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
          transition: "all 0.2s",
        }}
        onClick={() => setItemToPay(pro)}
      >
        💳 Payer
      </button>
    );
  };

  /* ─── RENDER ───────────────────────────────────────────────────── */
  return (
    <div style={s.wrapper}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cmd-row { animation: fadeUp 0.35s ease both; }
        .cmd-row:nth-child(1)  { animation-delay: 0.04s; }
        .cmd-row:nth-child(2)  { animation-delay: 0.08s; }
        .cmd-row:nth-child(3)  { animation-delay: 0.12s; }
        .cmd-row:nth-child(4)  { animation-delay: 0.16s; }
        .cmd-row:nth-child(5)  { animation-delay: 0.20s; }
        .cmd-row:nth-child(n+6){ animation-delay: 0.22s; }
        .cmd-row:hover td { background-color: #ffffe9 !important; }
        .back-btn:hover { background-color: rgba(255,196,0,0.1) !important; border-color: rgba(255,196,0,0.4) !important; }
        button:disabled { cursor: not-allowed; }
        .cmd-card { animation: fadeUp 0.3s ease both; }
        ::-webkit-scrollbar { height: 6px; width: 6px; }
        ::-webkit-scrollbar-track { background: #141720; }
        ::-webkit-scrollbar-thumb { background: #2e3447; border-radius: 3px; }
      `}</style>

      <NewSideBarMagasins />

      {confirmPopup && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(2px)"
        }}>
          <div style={{
            backgroundColor: "#fff", padding: "30px", borderRadius: "16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)", textAlign: "center", maxWidth: "400px", margin: "20px"
          }}>
            <div style={{ fontSize: "40px", marginBottom: "15px", animation: "spin 2s linear infinite" }}><i className="bi bi-hourglass-bottom"></i></div>
            <h3 style={{ margin: "0 0 10px 0", fontSize: "20px", color: C.textPrimary }}>Traitement en cours</h3>
            <p style={{ margin: 0, color: C.textSecondary, fontSize: "15px", lineHeight: "1.5" }}>
              Attendez la confirmation de la commande et aussi la mise à jour du stock...
            </p>
          </div>
        </div>
      )}

      <div style={s.main}>
        {/* Header */}
        <div style={s.header}>
          <button className="back-btn" onClick={handleGoBack} style={s.backBtn}>
            <i className="bi bi-arrow-left-short" />
          </button>
          <div style={s.titleWrapper}>
            <h4 style={s.title}>Détails des commandes</h4>
            <p style={s.subtitle}>
              {product.length > 0 ? `${product.length} article${product.length > 1 ? "s" : ""}` : "Aucun article"}
            </p>
            <div style={s.accentLine} />
          </div>
        </div>

        {/* Alerts */}
        {(MessageSucess || localSuccess) && (
          <div style={{ backgroundColor: C.greenDim, border: `1px solid rgba(34,197,94,0.3)`, color: C.green, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", fontSize: "14px", fontWeight: 500 }}>
            ✓ {MessageSucess || localSuccess}
          </div>
        )}
        {MessageError && (
          <div style={{ backgroundColor: C.redDim, border: `1px solid rgba(239,68,68,0.3)`, color: C.red, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", fontSize: "14px", fontWeight: 500 }}>
            ✕ {MessageError}
          </div>
        )}

        {loading ? (
          <Loader />
        ) : isMobile ? (
          /* ── MOBILE CARDS ─────────────────────────────── */
          <div>
            {product && product.length > 0 ? (
              product.map((pro, i) => (
                <div key={pro.idCommande} className="cmd-card" style={{ ...s.card, animationDelay: `${i * 0.06}s` }}>
                  <div style={s.cardHeader}>
                    <span style={s.idBadge}>#{pro.idCommande}</span>
                    <StatutBadge statut={pro.Statut} />
                  </div>
                  {[
                    { label: "Article", value: pro.NomArticle },
                    { label: "Référence", value: pro.RefArticle },
                    { label: "Vendeur", value: pro.vendeur },
                    { label: "Prix", value: `${pro.prix} MAD` },
                    { label: "Quantité", value: pro.quantity },
                    { label: "Date", value: pro.created_at },
                  ].map(({ label, value }) => (
                    <div key={label} style={s.cardRow}>
                      <span style={s.cardLabel}>{label}</span>
                      <span style={s.cardValue}>{value}</span>
                    </div>
                  ))}
                  <div style={{ padding: "12px 14px" }}>
                    {renderActions(pro)}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: "center", padding: "60px 20px", color: C.textMuted }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>📦</div>
                <p style={{ margin: 0, fontSize: "15px" }}>Aucune commande trouvée</p>
              </div>
            )}
          </div>
        ) : (
          /* ── DESKTOP TABLE ────────────────────────────── */
          <div style={s.tableCard}>
            <div style={{ overflowX: "auto" }}>
              <table style={s.table}>
                <thead style={s.thead}>
                  <tr>
                    {["N° Commande", "Référence", "Article", "Vendeur", "Prix", "Quantité", "Statut", "Action"].map((h) => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {product && product.length > 0 ? (
                    product.map((pro) => (
                      <tr key={pro.idCommande} className="cmd-row" style={{ transition: "background 0.15s" }}>
                        <td style={s.td}><span style={s.idBadge}>#{pro.idCommande}</span></td>
                        <td style={s.tdMuted}>{pro.RefArticle}</td>
                        <td style={{ ...s.td, fontWeight: 500 }}>{pro.NomArticle}</td>
                        <td style={s.td}>{pro.vendeur}</td>
                        <td style={{ ...s.td, ...s.priceBadge }}>{pro.prix} <span style={{ color: C.textMuted, fontSize: "12px" }}>MAD</span></td>
                        <td style={s.td}><span style={s.qtyBadge}>{pro.quantity}</span></td>
                        <td style={s.td}><StatutBadge statut={pro.Statut} /></td>
                        <td style={s.tdActions}>
                          {renderActions(pro)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} style={s.emptyRow}>
                        <div style={{ fontSize: "32px", marginBottom: "10px" }}>📦</div>
                        <div>Aucune commande trouvée</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Pay Article Modal */}
      <PayArticleModal
        isOpen={!!itemToPay}
        onClose={() => setItemToPay(null)}
        item={itemToPay}
        buyerType="magasin"
        buyerId={localStorage.getItem("MagasinId")}
        buyerName={MagasinName}
        onSuccess={(newStatut, newSolde) => {
          if (itemToPay) {
            setState((prev) => ({
              ...prev,
              product: prev.product.map((p) =>
                p.id === itemToPay.id ? { ...p, Statut: newStatut } : p
              ),
            }));
          }
          if (newSolde !== undefined && newSolde !== null) {
            localStorage.setItem("en_compte_solde", String(newSolde));
          }
          setLocalSuccess(`Article "${itemToPay?.NomArticle}" payé avec succès.`);
          setTimeout(() => setLocalSuccess(""), 4000);
        }}
      />
    </div>
  );
}